import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { setLocale as setI18nLocale, getCurrentLocale, availableLocales, type LocaleCode } from '@/i18n'

const API_BASE_URL = '/api/v1'

export const useI18nStore = defineStore('i18n', () => {
  // State
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  // Getters
  const currentLocale = computed(() => getCurrentLocale())
  
  const currentLocaleInfo = computed(() => {
    return availableLocales.find(l => l.code === currentLocale.value)
  })

  // Actions
  const changeLocale = async (locale: LocaleCode, syncWithBackend = true) => {
    try {
      isLoading.value = true
      error.value = null
      
      // Update local i18n
      setI18nLocale(locale)
      
      // Sync with backend if user is logged in
      if (syncWithBackend) {
        await syncLocaleWithBackend(locale)
      }
      
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to change locale'
      console.error('Failed to change locale:', err)
      return false
    } finally {
      isLoading.value = false
    }
  }

  const syncLocaleWithBackend = async (locale: LocaleCode) => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          user: { preferred_locale: locale }
        })
      })

      if (!response.ok) {
        // Don't throw for unauthorized (user not logged in)
        if (response.status === 401) {
          return
        }
        throw new Error('Failed to sync locale with server')
      }
    } catch (err) {
      // Silently fail - local storage is the source of truth
      console.warn('Could not sync locale with server:', err)
    }
  }

  // Load user's preferred locale from backend on init
  const loadUserLocale = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/user`, {
        credentials: 'include'
      })
      
      if (!response.ok) {
        return
      }
      
      const data = await response.json()
      if (data.user?.preferred_locale) {
        const locale = data.user.preferred_locale as LocaleCode
        if (availableLocales.some(l => l.code === locale)) {
          setI18nLocale(locale)
        }
      }
    } catch (err) {
      // Silently fail - use local storage fallback
      console.warn('Could not load user locale:', err)
    }
  }

  return {
    // State
    isLoading,
    error,
    
    // Getters
    currentLocale,
    currentLocaleInfo,
    availableLocales,
    
    // Actions
    changeLocale,
    loadUserLocale,
  }
})
